import axios from 'axios';

// ─── Customer code → GA4 portal key mapping ────────────────────────────────
export const CUSTOMER_TO_GA4_PORTAL: Record<string, string> = {
  ft: 'finanztrends',
  bg: 'boerse_global',
  sw: 'stock_world',
  tt: 'trading_treff',
  // TODO: re-enable after 2026-04-29 — GA4 data not clean during migration
  // be: "boerse_express",
  kme: 'kapitalmarktexperten',
};

const NO_LEADS_CUSTOMERS = new Set(['kme']);

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TrafficKpi {
  label: string;
  value: number;
  formattedValue: string;
  changePercent: number | null; // null when previous is 0
}

export interface TrafficChartData {
  dates: string[]; // "DD.MM" formatted
  users: number[]; // daily totalUsers
  leads: number[]; // daily organic leads (empty if !hasLeads)
}

export interface Ga4TrafficData {
  kpis: TrafficKpi[];
  chart: TrafficChartData;
  hasLeads: boolean;
  /** Display-formatted date range, e.g. "01.04. – 14.04.2026" */
  dateRangeLabel: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

// ─── Fetcher ────────────────────────────────────────────────────────────────

export async function fetchGa4TrafficData(customer: string): Promise<Ga4TrafficData | null> {
  const portalKey = CUSTOMER_TO_GA4_PORTAL[customer];
  if (!portalKey) return null;

  const hasLeads = !NO_LEADS_CUSTOMERS.has(customer);
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

  // Last 14 complete days: end at yesterday (today excluded — incomplete data)
  const now = new Date();
  const endDate = new Date(now);
  endDate.setDate(endDate.getDate() - 1);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 13); // 14 days total

  const start = formatDate(startDate);
  const end = formatDate(endDate);

  // Parallel API calls
  const overviewUrl = `${baseUrl}/api/ga4/overview?portal=${portalKey}&startDate=${start}&endDate=${end}&compare=1`;
  const timeseriesUrl = `${baseUrl}/api/ga4/timeseries?portal=${portalKey}&startDate=${start}&endDate=${end}&bucket=day`;
  const leadsUrl = hasLeads
    ? `${baseUrl}/api/ga4/leads?portal=${portalKey}&startDate=${start}&endDate=${end}&compare=1&bucket=day`
    : null;

  const promises = [
    axios.get(overviewUrl),
    axios.get(timeseriesUrl),
    ...(leadsUrl ? [axios.get(leadsUrl)] : []),
  ];

  const results = await Promise.all(promises);

  const overviewRes = results[0].data;
  const timeseriesRes = results[1].data;
  const leadsRes = leadsUrl ? results[2].data : null;

  if (!overviewRes.success || !timeseriesRes.success) {
    throw new Error(
      `GA4 API error: overview=${overviewRes.success}, timeseries=${timeseriesRes.success}`,
    );
  }

  const current = overviewRes.data.current;
  const previous = overviewRes.data.previous;

  // Build KPIs
  const kpis: TrafficKpi[] = [
    {
      label: 'Unique Users',
      value: current.totalUsers,
      formattedValue: formatNumber(current.totalUsers),
      changePercent: pctChange(current.totalUsers, previous.totalUsers),
    },
    {
      label: 'Sessions',
      value: current.sessions,
      formattedValue: formatNumber(current.sessions),
      changePercent: pctChange(current.sessions, previous.sessions),
    },
    {
      label: 'Page Views',
      value: current.pageViews,
      formattedValue: formatNumber(current.pageViews),
      changePercent: pctChange(current.pageViews, previous.pageViews),
    },
  ];

  // Lead KPIs (only for portals with leads)
  if (hasLeads && leadsRes?.success) {
    const currentLeads = leadsRes.data.current?.organicLeads ?? 0;
    const previousLeads = leadsRes.data.previous?.organicLeads ?? 0;
    const leadsPerKUsers = current.totalUsers > 0 ? (currentLeads / current.totalUsers) * 1000 : 0;
    const prevLeadsPerKUsers =
      previous.totalUsers > 0 ? (previousLeads / previous.totalUsers) * 1000 : 0;

    kpis.push({
      label: 'Traffic Leads',
      value: currentLeads,
      formattedValue: formatNumber(currentLeads),
      changePercent: pctChange(currentLeads, previousLeads),
    });
    kpis.push({
      label: 'Leads / 1k Users',
      value: leadsPerKUsers,
      formattedValue: formatNumber(leadsPerKUsers),
      changePercent: pctChange(leadsPerKUsers, prevLeadsPerKUsers),
    });
  }

  // Build chart data from timeseries
  const tsRows: Array<{ date: string; totalUsers: number }> = timeseriesRes.data;
  const leadsTimeseries: Array<{ date: string; leads: number }> =
    hasLeads && leadsRes?.success ? leadsRes.data.organicTimeseries || [] : [];

  // Build a leads lookup by date
  const leadsMap = new Map<string, number>();
  for (const row of leadsTimeseries) {
    leadsMap.set(row.date, row.leads);
  }

  const chart: TrafficChartData = {
    dates: tsRows.map((r) => {
      const parts = r.date.split('-');
      return `${parts[2]}.${parts[1]}`; // DD.MM
    }),
    users: tsRows.map((r) => r.totalUsers),
    leads: hasLeads ? tsRows.map((r) => leadsMap.get(r.date) ?? 0) : [],
  };

  // Build display label: "01.04. – 14.04.2026"
  const startDay = String(startDate.getDate()).padStart(2, '0');
  const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
  const endDay = String(endDate.getDate()).padStart(2, '0');
  const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
  const endYear = endDate.getFullYear();
  const dateRangeLabel = `${startDay}.${startMonth}. \u2013 ${endDay}.${endMonth}.${endYear}`;

  return { kpis, chart, hasLeads, dateRangeLabel };
}
